import translateI18 from '@/hooks/translateI18';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { computed, onBeforeMount, onMounted, reactive, ref, Ref } from 'vue';
import { Mutations } from '@/store/enums/StoreEnums';
import Swal from 'sweetalert2/dist/sweetalert2.min.js';
import { ReferralsUtil } from '@/assets/ts/_utils/ReferralsUtil';
import { modalErrorBackend, modalErrorTryAgain } from '@/assets/ts/_utils/SwalFireHelper';
import { globalUrl } from '@/core/config/DomainConfig';
import { inactivityTime } from '@/assets/ts/_utils/DomHelpers';

import { LoginModel } from '@/assets/ts/_utils/models/LoginModel';
import ApiService from '@/core/services/ApiService';
import { AuthDispatcherService } from '@/store/services/AuthDispatcherService';
import ErrorHandlerUtil from '@/assets/ts/_utils/ErrorHandlerUtil';
import { ErrorGetters } from '@/store/enums/ErrorGettersEnums';
import UserDispatcherService from '@/store/services/UserDispatcherService';

export function SignIn(props, { emit }) {
  // region Dependency region
  const { translate } = translateI18();
  const store = useStore();
  const router = useRouter();
  const route = useRoute();
  const ErrorHandler = new ErrorHandlerUtil(store);
  const AuthDispatcher = new AuthDispatcherService(store);
  const UserDispatcher = new UserDispatcherService(store);
  // endregion

  const query = ref({});
  const submitButton = ref<Ref | null>(null);
  const googleLink = ref(`${globalUrl}auth/google`);
  const facebookLink = ref(`${globalUrl}auth/facebook`);
  const appleLink = ref(`${globalUrl}auth/apple`);
  const signUpLink = ref('/signup');
  const pageLoaded = ref(false);
  const password = ref();
  const emailRef = ref<Ref | null>(null);
  const formRef = ref<null | Ref>(null);
  const targetData = ref<LoginModel>({
    emailField: '',
    passwordField: ''
  });
  const loginRules = reactive({
    emailField: [
      { required: true, message: translate('register.loginErrorEmail'), trigger: ['change', 'blur'] },
      { type: 'email', message: translate('register.loginErrorEmailValid'), trigger: ['change', 'blur'] }
    ],
    passwordField: [
      { required: true, message: translate('register.loginErrorPassword'), trigger: ['change', 'blur'] },
      { min: 3, message: translate('register.loginErrorPasswordMin3'), trigger: ['change', 'blur'] }
    ]
  });
  const computedGoogleLink = computed(() => {
    return ReferralsUtil.generateLink(googleLink.value, query.value);
  });

  const computedFacebookLink = computed(() => {
    return ReferralsUtil.generateLink(facebookLink.value, query.value);
  });

  const computedAppleLink = computed(() => {
    return ReferralsUtil.generateLink(appleLink.value, query.value);
  });

  const computedSignUpLink = computed(() => {
    return ReferralsUtil.generateLink(signUpLink.value, query.value);
  });


  onBeforeMount(async () => {
    await AuthDispatcher.getError();
    const errors = store.getters[ErrorGetters.GET_TEXT_ERROR];
    if (errors) {
      ErrorHandler.handle(ErrorGetters.GET_TEXT_ERROR, Mutations.SET_TEXT_ERROR, () => {});
    }
  });


  onMounted(() => {
    ApiService?.resetRunningRequests();
    const keys = Object.keys(route.query);
    for (let i = 0; i < keys.length; i++) {
      query.value[keys[i]] = route.query[keys[i]];
    }
    pageLoaded.value = true;

    setTimeout(() => {
      if (emailRef.value) {
        emailRef.value.customInput.focus();
      }
    }, 300);

  });
  const reSendVerificationNotification = () => {

  };
  const loginProcess = async () => {
    ApiService.resetController();

    // Clear existing errors
    store.commit(Mutations.PURGE_AUTH);
    if (submitButton.value) {
            // eslint-disable-next-line
            submitButton.value!.disabled = true;
            // Activate indicator
            submitButton.value.setAttribute('data-kt-indicator', 'on');
    }

    const params = {
      email: targetData.value.emailField,
      password: targetData.value.passwordField,
    };

    for (const [key, value] of Object.entries(query.value)) {
      params[key] = value;
    }

    // Send login request
    await AuthDispatcher.login(params);
    const errors = store.getters.getErrorsAuth;

    if (Object.keys(errors)?.length === 0) {
      const userId = UserDispatcher.getValidatedCurrentUser()?.id;
      if (userId) {
        window.dataLayer.push({ 'event': 'login', 'user_id': userId });
      }
      inactivityTime();
      const page = route.query.page;
      router.push({ name: 'starting', query: { page } });
    } else {
      //Deactivate indicator
      if (submitButton.value) {
        submitButton.value?.removeAttribute('data-kt-indicator');
                // eslint-disable-next-line
                submitButton.value!.disabled = false;
      }
      if (errors.type) {
        const swalWithBootstrapButtons = Swal.mixin({
          customClass: {
            confirmButton: 'btn btn-success',
            cancelButton: 'btn btn-danger'
          },
          buttonsStyling: false
        });
        swalWithBootstrapButtons.fire({
          title: translate('register.yourAccountIsNotActivated'),
          html: ` <input type="email" id="email-form" hidden value="${targetData.value.emailField}">`,
          confirmButtonText: 'RE-SEND',
          showCloseButton: true,
          showCancelButton: true,
          focusConfirm: false,
          reverseButtons: true,
          didOpen: (modal) => {
            modal.id = 'resend-email-modal';
          },
          preConfirm: () => {
            const email = Swal.getPopup().querySelector('#email-form').value;

            return { email };
          }
        }).then(async (result) => {
          if (result.isConfirmed) {
            await AuthDispatcher.emailVerificationNotification(result.value.email);
            Swal.fire(translate('register.activationEmeilWasSuccessfullySent'));
          }
        });
      } else {
        modalErrorBackend(errors);
      }
    }
    store.commit(Mutations.SET_ERROR, {});
  };
    //Form submit function
  const onSubmitLogin = async () => {
    if (!formRef.value.customForm) {
      return;
    }
    formRef.value.customForm.validate((valid) => {
      if (valid) {
        return loginProcess();
      } else {
        return modalErrorTryAgain();
      }
    });


  };

  return {
    formRef,
    targetData,
    onSubmitLogin,
    password,
    emailRef,
    loginRules,
    pageLoaded,
    submitButton,
    translate,
    computedSignUpLink,
    computedGoogleLink,
    computedFacebookLink,
    computedAppleLink
  };
}
